import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  QrCode, UserRound, Plus, Gift, CheckCircle2, ScanLine, Loader2, BarChart3,
  History, ShieldAlert, Trash2, LogOut, X, Maximize2, Minimize2, ChevronLeft,
} from "lucide-react";
import {
  getCustomers, getState, getMyContext, addStamp, redeemReward, getStats, getAuditLog, resetPilot, getStaffLoyaltyActions, setLoyaltyRequestAccess,
} from "@/api";
import { useAuth } from "@/AuthContext";
import { BrandScope } from "@/theme";
import { BusinessLayout, EnvBadge, Loading } from "./Layout";
import { ChangePasswordButton } from "./ChangePasswordButton";
import { QrScanner } from "./QrScanner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";

const RESET_PHRASE = "RESET PILOT";

// Mirrors Python's round() (half-to-even) so the preview equals the server result.
const pyRound = (x) => {
  const f = Math.floor(x);
  const d = x - f;
  if (d > 0.5) return f + 1;
  if (d < 0.5) return f;
  return f % 2 === 0 ? f : f + 1;
};

// Full operational cockpit. Requires a `business` object for branding + mode.
export const ShopCockpit = ({ business, canReset = false, onCounterViewChange }) => {
  const isPoints = business.loyalty_mode === "points_spend";
  const isSpend = business.loyalty_mode === "stamp_spend";
  const needsAmount = isPoints || isSpend;
  const unit = isPoints ? "Punkte" : "Stempel";
  const cur = business.currency || "€";

  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [state, setState] = useState(null);
  const [stampConfirm, setStampConfirm] = useState(false);
  const [amount, setAmount] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [redeemTarget, setRedeemTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [search, setSearch] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPhrase, setResetPhrase] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [auditDate, setAuditDate] = useState("");
  const [auditType, setAuditType] = useState("");
  const actionIdRef = useRef(null);
  const boundActionRef = useRef(null); // {customer_id, action_id} from a selected/scanned pending loyalty action
  const [pendingActions, setPendingActions] = useState([]);
  const [, setTick] = useState(0);
  // Phone Thekenansicht only: true after an intentional pick (not the default list[0] auto-selection).
  const [phonePicked, setPhonePicked] = useState(false);

  const loadCustomers = useCallback(async () => {
    const list = await getCustomers();
    setCustomers(list);
    setSelected((prev) => (list.find((c) => c.id === prev) ? prev : (list[0] && list[0].id)));
    return list;
  }, []);
  const loadState = useCallback(async (id) => {
    if (!id) return setState(null);
    try { setState(await getState(id)); } catch (e) { setState(null); }
  }, []);
  const loadStats = useCallback(async () => { try { setStats(await getStats()); } catch (e) {} }, []);
  const loadAudit = useCallback(async () => {
    try {
      const params = {};
      if (auditDate) params.date = auditDate;
      if (auditType) params.event_type = auditType;
      setAuditLog(await getAuditLog(params));
    } catch (e) {}
  }, [auditDate, auditType]);

  useEffect(() => { loadCustomers(); loadStats(); }, [loadCustomers, loadStats]);
  useEffect(() => { loadAudit(); }, [loadAudit]);
  useEffect(() => { loadState(selected); }, [selected, loadState]);

  // Phase 1: poll pending customer-initiated loyalty actions (non-blocking).
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await getStaffLoyaltyActions();
        if (!alive) return;
        setPendingActions(r);
        // Drop a stale binding if its action no longer appears as pending
        // (expired, confirmed elsewhere, or otherwise gone). Does not touch selection.
        const b = boundActionRef.current;
        if (b && !r.some((a) => a.customer_id === b.customer_id && a.action_id === b.action_id)) {
          boundActionRef.current = null;
        }
      } catch (e) {}
    };
    load();
    const iv = setInterval(load, 3000);
    const tk = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { alive = false; clearInterval(iv); clearInterval(tk); };
  }, []);

  const selectPending = (a) => {
    boundActionRef.current = { customer_id: a.customer_id, action_id: a.action_id };
    setSelected(a.customer_id);
    setPhonePicked(true);
  };

  const toggleRequestAccess = async () => {
    if (!selected || !state) return;
    const next = !state.customer.requests_blocked;
    try {
      await setLoyaltyRequestAccess(selected, next);
      toast.success(next ? "Kundenanfragen gesperrt." : "Kundenanfragen wieder erlaubt.");
      loadState(selected);
    } catch (e) { toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen."); }
  };

  const refresh = useCallback(async () => {
    const list = await loadCustomers();
    const id = list.find((c) => c.id === selected) ? selected : (list[0] && list[0].id);
    if (id) await loadState(id);
  }, [loadCustomers, loadState, selected]);

  const openStampConfirm = () => {
    const b = boundActionRef.current;
    actionIdRef.current = (b && b.customer_id === selected)
      ? b.action_id
      : ((crypto.randomUUID && crypto.randomUUID()) || String(Date.now() + Math.random()));
    setAmount(""); setQuantity(1);
    setStampConfirm(true);
  };

  const onQrResolved = (custObj) => {
    // Only treat as success when the backend returned a REAL customer id.
    const id = custObj?.customer_id || custObj?.id;
    if (!id) {
      toast.error("Kunde konnte nicht geladen werden.");
      return;
    }
    setScannerOpen(false);
    setSelected(id);
    setPhonePicked(true);
    // Digital QR only: if a pending loyalty action exists for this customer, reuse its
    // server-issued action_id so scan + request converge on one idempotent transaction.
    if (custObj.source === "digital") {
      const match = pendingActions.find((a) => a.customer_id === id);
      boundActionRef.current = match ? { customer_id: id, action_id: match.action_id } : null;
    }
    // reflect the newly created/linked customer everywhere: selection + list + stats + log
    loadCustomers();
    loadStats();
    loadAudit();
    toast.success(`Kunde gefunden: ${custObj.name || "Kunde"}`);
  };

  const doAddStamp = async () => {
    if (submitting) return;
    if (needsAmount && (!amount || parseFloat(amount) <= 0)) { toast.error("Bitte Betrag eingeben."); return; }
    setSubmitting(true);
    try {
      const opts = needsAmount ? { amount: parseFloat(amount) } : { quantity: parseInt(quantity, 10) || 1 };
      const res = await addStamp(selected, actionIdRef.current, opts);
      setStampConfirm(false);
      if (res.duplicate) toast.info("Buchung wurde bereits erfasst.");
      else if (res.reward_created) toast.success(`Belohnung freigeschaltet! 🎉`);
      else if (isPoints) toast.success(`+${res.result?.points_added || 0} Punkte hinzugefügt`);
      else toast.success(`+${res.result?.stamps_added || 0} ${unit} hinzugefügt`);
      refresh(); loadStats(); loadAudit();
      if (boundActionRef.current?.customer_id === selected) boundActionRef.current = null;
    } catch (e) {
      setStampConfirm(false);
      toast.error(e?.response?.data?.detail || "Buchung fehlgeschlagen.");
      refresh();
    } finally { setSubmitting(false); }
  };

  const doRedeem = async () => {
    try {
      await redeemReward(redeemTarget.id);
      setRedeemTarget(null);
      toast.success("Belohnung eingelöst ✓");
      refresh(); loadStats(); loadAudit();
    } catch (e) { setRedeemTarget(null); toast.error("Einlösen fehlgeschlagen."); refresh(); }
  };

  const doReset = async () => {
    if (resetBusy || resetPhrase.trim() !== RESET_PHRASE) return;
    setResetBusy(true);
    try {
      const res = await resetPilot(resetPhrase.trim());
      setResetOpen(false); setResetPhrase("");
      const d = res.deleted || {};
      toast.success(`Zurückgesetzt: ${d.customers || 0} Kunden, ${d.rewards || 0} Belohnungen gelöscht.`);
      refresh(); loadStats(); loadAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset fehlgeschlagen.");
    } finally { setResetBusy(false); }
  };

  const availableRewards = state?.rewards?.filter((r) => r.status === "available") || [];
  const cur_val = state?.progress?.current ?? 0;
  const tgt_val = state?.progress?.target ?? 0;
  const pct = state?.progress?.percent ?? 0;
  // UI-only helpers for the pending-request panel (reuse existing pendingActions state).
  // Booking consequence preview — same formulas as server apply_purchase().
  const amtNum = parseFloat(amount);
  const amtOk = needsAmount && amtNum > 0;
  const qtyNum = parseInt(quantity, 10) || 1;
  let creditN = null;
  if (isPoints && amtOk) creditN = pyRound(amtNum * Number(business.points_per_currency ?? 1));
  else if (isSpend && amtOk) creditN = Math.floor((Number(state?.customer?.remainder || 0) + amtNum) / (Number(business.spend_per_stamp) || 1));
  else if (!needsAmount && qtyNum >= 1 && qtyNum <= 50) creditN = qtyNum;
  const creditUnit = (n) => (isPoints ? (n === 1 ? "Punkt" : "Punkte") : "Stempel");
  const creditText = creditN != null ? `${creditN} ${creditUnit(creditN)}` : null;
  const amountText = amtOk ? `${amtNum.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 3 })} ${cur}` : null;
  const fmtMMSS = (secs) => { const s = Math.max(0, secs); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };
  const actionModeLabel = isPoints ? "Punkte sammeln" : "Stempel sammeln";
  const selectedPending = pendingActions.find((a) => a.customer_id === selected);
  const selectedPendingSecs = selectedPending
    ? Math.max(0, Math.round((new Date(selectedPending.expires_at).getTime() - Date.now()) / 1000))
    : 0;

  // Thekenansicht: presentation-only large layout, remembered per business on this device.
  const [counterView, setCounterView] = useState(() => {
    try { return localStorage.getItem(`bs_counter:${business.slug}`) === "1"; } catch (e) { return false; }
  });
  const toggleCounter = () => setCounterView((v) => {
    const nv = !v;
    try { localStorage.setItem(`bs_counter:${business.slug}`, nv ? "1" : "0"); } catch (e) {}
    return nv;
  });
  // App-shell only when the staff host opts in (OwnerDashboard keeps its layout).
  const shellMode = !!onCounterViewChange && counterView;
  useEffect(() => { if (onCounterViewChange) { onCounterViewChange(counterView); } }, [counterView, onCounterViewChange]);
  const phoneFlow = shellMode;
  const pickB = phoneFlow && phonePicked;
  const pf = (phone, base) => (phoneFlow ? phone : base);

  return (
    <div className={shellMode ? "p-3 lg:p-4 flex-1 flex flex-col lg:min-h-0" : counterView ? "p-3 lg:p-4" : "p-4 lg:p-6 pb-24 mx-auto space-y-4 lg:space-y-6 max-w-6xl"}>
      {counterView ? (
      <div className={shellMode ? "flex flex-col flex-1 lg:min-h-0 gap-3" : "flex flex-col h-[calc(100vh-140px)] min-h-[520px] gap-3"}>
        <div className={`flex items-center gap-2 shrink-0 ${pickB ? "justify-between lg:justify-end" : "justify-end"}`}>
          {pickB && (
            <Button data-testid="phone-choose-other-customer" onClick={() => setPhonePicked(false)} variant="outline"
              className="lg:hidden flex-1 rounded-xl font-display font-bold press border-2 h-12 px-3 text-base min-w-0 justify-start">
              <ChevronLeft className="mr-1 h-5 w-5 shrink-0" /> <span className="truncate">Anderen Kunden wählen</span>
            </Button>
          )}
          <Button data-testid="toggle-counter-view" onClick={toggleCounter} variant="outline"
            className={`rounded-xl font-display font-bold press border-2 h-12 text-base shrink-0 ${pf("px-3 lg:px-5", "px-5")}`}
            aria-label="Standardansicht" title="Standardansicht"
            style={{ borderColor: "var(--brand-primary)", color: "var(--brand-primary)" }}>
            <Minimize2 className={pickB ? "lg:mr-2 h-5 w-5" : "mr-2 h-5 w-5"} /> <span className={pickB ? "hidden lg:inline" : ""}>Standardansicht</span>
          </Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-4 flex-1 min-h-0 w-full">
          {/* LEFT: customer selection */}
          <div className={`${pickB ? "hidden lg:flex" : "flex"} flex-col gap-3 min-h-0 min-w-0`}>
            <Button data-testid="staff-scan-qr-button" onClick={() => setScannerOpen(true)}
              className={`rounded-2xl font-display font-bold flex-col gap-1 shadow-brand press text-white shrink-0 ${pf("h-20 text-xl lg:h-24 lg:text-2xl", "h-24 text-2xl")}`}
              style={{ background: "var(--brand-primary)" }}>
              <ScanLine className="h-9 w-9" /> QR-Code scannen
            </Button>
            <input data-testid="staff-customer-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Kunde suchen…"
              className="w-full rounded-xl ring-1 ring-slate-200 bg-white px-4 h-14 text-lg font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-[var(--brand-primary)] shrink-0" />
            {search.trim() && (
              <div data-testid="staff-customer-search-results" className="rounded-xl ring-1 ring-slate-100 bg-white divide-y divide-slate-50 overflow-y-auto max-h-48 shrink-0">
                {customers.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase())).map((c) => (
                  <button key={c.id} type="button" data-testid={`staff-customer-result-${c.id}`} onClick={() => { setSelected(c.id); setSearch(""); setPhonePicked(true); }}
                    className="w-full text-left px-4 py-3 press hover:bg-slate-50 flex items-center justify-between gap-2">
                    <span className="font-display font-bold text-slate-900 text-lg truncate">{c.name}</span>
                    <span className="text-sm font-semibold text-slate-400 shrink-0">{c.current}/{c.target} · #{c.id.slice(0, 4)}</span>
                  </button>
                ))}
                {customers.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase())).length === 0 && (
                  <p className="px-4 py-3 text-base text-slate-400 text-center">Kein Kunde gefunden.</p>
                )}
              </div>
            )}
            <p className="text-sm uppercase tracking-wider text-amber-600 font-bold flex items-center gap-1.5 shrink-0 mt-1">
              <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse-dot" /> Offene Anfragen ({pendingActions.length})
            </p>
            <div className="flex-1 min-h-0 overflow-y-auto space-y-2 pr-1">
              {pendingActions.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-200 p-4 text-center text-slate-400 text-base">Keine offenen Anfragen.</div>
              )}
              {pendingActions.map((a) => {
                const secs = Math.max(0, Math.round((new Date(a.expires_at).getTime() - Date.now()) / 1000));
                return (
                  <button key={a.id} type="button" data-testid={`pending-action-${a.customer_id}`} onClick={() => selectPending(a)}
                    className={`w-full text-left rounded-2xl px-4 py-3 ring-1 press transition-colors flex items-center justify-between gap-3 min-h-[64px] ${selected === a.customer_id ? "ring-2 ring-[var(--brand-primary)] bg-amber-50/50" : "ring-amber-100 bg-white hover:bg-amber-50/60"}`}>
                    <span className="min-w-0">
                      <span className={`block font-display font-extrabold text-slate-900 leading-tight truncate ${pf("text-[22px] lg:text-[28px]", "text-[28px]")}`}>{a.name}</span>
                      <span className={`block font-semibold text-slate-400 truncate ${pf("text-[15px] lg:text-[18px]", "text-[18px]")}`}>#{a.customer_id.slice(0, 4)} · {actionModeLabel}</span>
                    </span>
                    {secs > 0 && <span className={`${pf("text-[22px] lg:text-[28px]", "text-[28px]")} font-extrabold text-amber-600 shrink-0 tabular-nums text-right`}>{fmtMMSS(secs)}</span>}
                  </button>
                );
              })}
            </div>
          </div>
          {/* RIGHT: one unified customer panel */}
          <div className={`${phoneFlow && !phonePicked ? "hidden lg:block " : ""}min-h-0 min-w-0`}>
            {!state ? (
              <div className="h-full rounded-[22px] border border-dashed border-slate-200 bg-white flex flex-col items-center justify-center text-center text-slate-400 p-8">
                <UserRound className="h-12 w-12 mb-3 opacity-50" />
                <p className="text-xl font-semibold">Karte scannen oder eine Anfrage wählen.</p>
              </div>
            ) : (
              <div data-testid="staff-customer-info" className="h-full rounded-[22px] ring-1 ring-slate-100 shadow-soft bg-white p-5 flex flex-col min-h-0">
                <div className={`flex gap-3 shrink-0 ${pf("flex-col items-start lg:flex-row lg:items-start lg:justify-between", "items-start justify-between")}`}>
                  <div className={`min-w-0 ${pf("max-w-full", "")}`}>
                    <p className={`font-display font-extrabold text-slate-900 leading-tight truncate ${pf("text-[26px] lg:text-[34px]", "text-[34px]")}`}>{state.customer.name}</p>
                    <p className="text-lg font-semibold text-slate-400">#{state.customer.id.slice(0, 4)}</p>
                  </div>
                  {selectedPending && selectedPendingSecs > 0 && (
                    <span data-testid="selected-active-request" className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-amber-50 ring-1 ring-amber-200 px-3 py-1.5 text-base font-bold text-amber-600 tabular-nums">
                      <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse-dot" /> Anfrage · {fmtMMSS(selectedPendingSecs)}
                    </span>
                  )}
                </div>
                <p data-testid="staff-stamp-count" className={`font-display font-extrabold mt-2 shrink-0 ${pf("text-[26px] lg:text-[30px]", "text-[30px]")}`} style={{ color: "var(--brand-primary)" }}>
                  {cur_val} <span className="text-slate-300">/ {tgt_val}</span> <span className="text-xl font-bold text-slate-400">{unit}</span>
                </p>
                <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden mt-2 shrink-0">
                  <motion.div className="h-full rounded-full brand-wave" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }} />
                </div>
                <Button data-testid="staff-add-stamp-button" onClick={openStampConfirm} disabled={submitting}
                  className="w-full rounded-2xl mt-4 h-[68px] text-2xl font-display font-bold shadow-brand disabled:opacity-60 press text-white shrink-0"
                  style={{ background: "var(--brand-primary)" }}>
                  {submitting ? <><Loader2 className="mr-2 h-6 w-6 animate-spin" /> Wird gebucht…</> : <><Plus className="mr-2 h-7 w-7" /> {needsAmount ? "Einkauf buchen" : `+1 ${unit}`}</>}
                </Button>
                <div className="mt-4 rounded-2xl bg-slate-50 ring-1 ring-slate-100 p-3 flex-1 min-h-0 flex flex-col">
                  <p className="text-sm uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1.5 shrink-0 mb-2">
                    <Gift className="h-4 w-4" style={{ color: "var(--brand-secondary)" }} /> Belohnungen ({availableRewards.length})
                  </p>
                  {availableRewards.length === 0 ? (
                    <p className="text-base text-slate-400 font-medium">Noch keine Belohnung verfügbar.</p>
                  ) : (
                    <div className="space-y-2 overflow-y-auto pr-1">
                      {availableRewards.map((r) => (
                        <div key={r.id} data-testid="staff-reward-item" className="rounded-xl p-3 ring-1 ring-slate-100 bg-white flex items-center justify-between gap-3">
                          <p className="font-display font-extrabold text-lg text-slate-900 leading-tight truncate">{r.title} <span className="text-emerald-600 font-bold">verfügbar</span></p>
                          <Button data-testid="staff-redeem-reward-button" onClick={() => setRedeemTarget(r)} className={`rounded-xl bg-emerald-600 hover:bg-emerald-700 font-bold shadow-sm press shrink-0 h-14 text-lg ${pf("px-4 lg:px-6", "px-6")}`}>Einlösen</Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {canReset && (
                  <button type="button" data-testid="toggle-request-access" onClick={toggleRequestAccess}
                    className="w-full mt-2 text-sm font-bold text-slate-400 hover:text-slate-600 press shrink-0">
                    {state.customer.requests_blocked ? "Kundenanfragen wieder erlauben" : "Kundenanfragen sperren"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      ) : (<>
      <div className="flex justify-end">
        <Button data-testid="toggle-counter-view" onClick={toggleCounter} variant="outline"
          className="rounded-xl font-display font-bold press border-2 h-9 text-xs px-3"
          style={{ borderColor: "var(--brand-primary)", color: "var(--brand-primary)" }}>
          <Maximize2 className="mr-2 h-4 w-4" /> Thekenansicht öffnen
        </Button>
      </div>
      {/* Action bar: scanner + customer selection (prominent, full width) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Button data-testid="staff-scan-qr-button" onClick={() => setScannerOpen(true)}
          className={`rounded-2xl font-display font-bold flex-col gap-1 shadow-brand press text-white ${counterView ? "h-28 text-2xl" : "h-[68px] lg:h-24 text-base lg:text-lg"}`}
          style={{ background: "var(--brand-primary)" }}>
          <ScanLine className={counterView ? "h-9 w-9" : "h-5 w-5 lg:h-7 lg:w-7"} /> QR-Code scannen
        </Button>
        <div className="rounded-2xl ring-1 ring-slate-200 bg-white p-3 lg:p-4 flex flex-col justify-center shadow-soft">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold px-1 mb-0.5">Kunde</p>
          <input data-testid="staff-customer-search" value={search}
            onChange={(e) => setSearch(e.target.value)} placeholder="Kunde suchen…"
            className="w-full mb-1.5 rounded-xl ring-1 ring-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-[var(--brand-primary)]" />
          {search.trim() ? (
            <div data-testid="staff-customer-search-results" className="max-h-52 overflow-y-auto rounded-xl ring-1 ring-slate-100 divide-y divide-slate-50">
              {customers
                .filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()))
                .map((c) => (
                  <button key={c.id} type="button" data-testid={`staff-customer-result-${c.id}`}
                    onClick={() => { setSelected(c.id); setSearch(""); }}
                    className={`w-full text-left px-3 py-2.5 press hover:bg-slate-50 flex items-center justify-between gap-2 ${selected === c.id ? "bg-slate-50" : ""}`}>
                    <span className="font-display font-bold text-slate-900 text-sm truncate">{c.name}</span>
                    <span className="text-xs font-semibold text-slate-400 shrink-0">{c.current}/{c.target} · #{c.id.slice(0, 4)}</span>
                  </button>
                ))}
              {customers.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase())).length === 0 && (
                <p data-testid="staff-customer-search-empty" className="px-3 py-3 text-sm text-slate-400 text-center">Kein Kunde gefunden.</p>
              )}
            </div>
          ) : (
          <select data-testid="staff-customer-select" value={selected || ""}
            onChange={(e) => setSelected(e.target.value)}
            className="w-full bg-transparent font-display font-bold text-slate-900 outline-none px-1 lg:text-lg">
            <option value="" disabled>Auswählen…</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{`${c.name} · ${c.current}/${c.target} · #${c.id.slice(0, 4)}`}</option>
            ))}
          </select>
          )}
        </div>
      </div>

      {/* Offene Kundenanfragen — kept high, directly under the scan/search bar. Hidden when empty. */}
      {pendingActions.length > 0 && (
        <div data-testid="pending-actions-panel" className="rounded-2xl bg-white ring-1 ring-amber-200 shadow-soft p-4">
          <p className="text-[11px] uppercase tracking-wider text-amber-600 font-bold flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse-dot" /> Offene Kundenanfragen</p>
          <p className="text-xs text-slate-400 font-semibold mt-0.5 mb-2.5">Anfrage antippen oder QR-Code scannen.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {pendingActions.map((a) => {
              const secs = Math.max(0, Math.round((new Date(a.expires_at).getTime() - Date.now()) / 1000));
              return (
                <button key={a.id} type="button" data-testid={`pending-action-${a.customer_id}`} onClick={() => selectPending(a)}
                  className={`w-full text-left rounded-xl px-3 py-2.5 ring-1 press transition-colors hover:bg-amber-50/60 flex items-center justify-between gap-2 ${selected === a.customer_id ? "ring-[var(--brand-primary)] bg-amber-50/50" : "ring-amber-100 bg-white"}`}>
                  <span className="min-w-0">
                    <span className="block font-display font-bold text-slate-900 text-[15px] truncate">{a.name}</span>
                    <span className="block text-[11px] font-semibold text-slate-400 truncate">#{a.customer_id.slice(0, 4)} · {actionModeLabel}</span>
                  </span>
                  {secs > 0 && <span className="text-sm font-extrabold text-amber-600 shrink-0 tabular-nums">{fmtMMSS(secs)}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Two-column cockpit on tablet/desktop; single column on mobile (and in Thekenansicht) */}
      <div className={`grid grid-cols-1 gap-4 lg:gap-6 items-start ${counterView ? "" : "lg:grid-cols-3"}`}>
        {/* Primary workflow: selected customer card + action + rewards */}
        <div className={`space-y-4 ${counterView ? "" : "lg:col-span-2"}`}>
      {!state && (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-8 text-center text-slate-400">
          <UserRound className="h-8 w-8 mx-auto mb-2 opacity-50" />
          Wähle einen Kunden oder scanne einen QR-Code.
        </div>
      )}

      {state && (
        <>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold px-1">Ausgewählter Kunde</p>
          <div data-testid="staff-customer-info" className="bg-white rounded-[22px] ring-1 ring-slate-100 shadow-soft p-5">
            <div className="flex items-center gap-3.5">
              <div className={`rounded-2xl brand-wave flex items-center justify-center shadow-sm shrink-0 ${counterView ? "h-20 w-20" : "h-14 w-14"}`}>
                <span className={`text-white font-display font-extrabold ${counterView ? "text-4xl" : "text-xl"}`}>{state.customer.name[0]}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className={`font-display font-extrabold text-slate-900 leading-tight truncate ${counterView ? "text-4xl" : "text-xl"}`}>{state.customer.name}</p>
                  {selectedPending && selectedPendingSecs > 0 && (
                    <span data-testid="selected-active-request" className={`shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-50 ring-1 ring-amber-200 font-bold text-amber-600 tabular-nums ${counterView ? "px-3 py-1.5 text-sm" : "px-2.5 py-1 text-[11px]"}`}>
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse-dot" /> Aktive Anfrage · {fmtMMSS(selectedPendingSecs)}
                    </span>
                  )}
                </div>
                <p data-testid="staff-stamp-count" className={`font-display font-extrabold ${counterView ? "text-3xl mt-1" : "text-lg"}`} style={{ color: "var(--brand-primary)" }}>
                  {cur_val} <span className="text-slate-300">/ {tgt_val}</span> <span className={`font-bold text-slate-400 ${counterView ? "text-xl" : "text-sm"}`}>{unit}</span>
                </p>
              </div>
            </div>
            <div className="h-3 w-full rounded-full bg-slate-100 overflow-hidden mt-4">
              <motion.div className="h-full rounded-full brand-wave" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6, ease: "easeOut" }} />
            </div>
            <Button data-testid="staff-add-stamp-button" onClick={openStampConfirm} disabled={submitting}
              className={`w-full rounded-2xl mt-4 font-display font-bold shadow-brand disabled:opacity-60 press text-white ${counterView ? "h-[72px] text-2xl" : "h-14 text-base"}`}
              style={{ background: "var(--brand-primary)" }}>
              {submitting ? <><Loader2 className="mr-1 h-5 w-5 animate-spin" /> Wird gebucht…</> : <><Plus className={counterView ? "mr-1 h-7 w-7" : "mr-1 h-5 w-5"} /> {needsAmount ? "Einkauf buchen" : `+1 ${unit}`}</>}
            </Button>
            {canReset && (
              <button type="button" data-testid="toggle-request-access" onClick={toggleRequestAccess}
                className="w-full mt-2 text-xs font-bold text-slate-400 hover:text-slate-600 press">
                {state.customer.requests_blocked ? "Kundenanfragen wieder erlauben" : "Kundenanfragen sperren"}
              </button>
            )}
          </div>

          <div className="space-y-3">
            <h2 className="font-display font-extrabold text-[17px] text-slate-900 flex items-center gap-2 min-w-0">
              <Gift className="h-[18px] w-[18px] shrink-0" style={{ color: "var(--brand-secondary)" }} /> <span className="truncate">Belohnungen von {state.customer.name}</span>
            </h2>
            {availableRewards.length === 0 && (
              <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-5 text-center text-slate-400 text-sm">Keine offenen Belohnungen.</div>
            )}
            {availableRewards.map((r) => (
              <div key={r.id} data-testid="staff-reward-item" className="rounded-2xl p-4 ring-1 ring-slate-100 bg-white shadow-soft flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl brand-wave flex items-center justify-center shadow-sm shrink-0"><Gift className="h-6 w-6 text-white" /></div>
                  <p className="font-display font-extrabold text-[15px] text-slate-900 leading-tight">{r.title}</p>
                </div>
                <Button data-testid="staff-redeem-reward-button" onClick={() => setRedeemTarget(r)} className="rounded-xl bg-emerald-600 hover:bg-emerald-700 font-bold shadow-sm press shrink-0">Einlösen</Button>
              </div>
            ))}
          </div>
        </>
      )}
        </div>{/* end primary column */}

        {/* Secondary column: QR poster, compact statistics, activity log, owner admin — hidden in Thekenansicht */}
        {!counterView && (
        <div className="space-y-4">
      <div className="rounded-2xl bg-white ring-1 ring-slate-100 shadow-soft p-4">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">QR-Aushang</p>
        <Button data-testid="open-poster-button" onClick={() => window.open(`/poster/${business.slug}`, "_blank")}
          variant="outline" className="w-full h-12 rounded-2xl font-display font-bold press"
          style={{ borderColor: "var(--brand-primary)", color: "var(--brand-primary)" }}>
          <QrCode className="mr-2 h-4 w-4" /> QR-Aushang öffnen
        </Button>
      </div>

      {stats && (
        <div data-testid="staff-stats-section" className="rounded-2xl bg-white ring-1 ring-slate-100 shadow-soft p-4 space-y-3">
          <h2 className="font-display font-extrabold text-[15px] text-slate-900 flex items-center gap-2">
            <BarChart3 className="h-[18px] w-[18px]" style={{ color: "var(--brand-primary)" }} /> Statistik
          </h2>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Heute</p>
            <div className="grid grid-cols-2 gap-2">
              <StatCell testid="stat-today-stamps" value={isPoints ? stats.today.points : stats.today.stamps} label={isPoints ? "Punkte heute" : "Stempel heute"} />
              <StatCell testid="stat-today-new-customers" value={stats.today.new_customers} label="Neue Kunden heute" />
              <StatCell testid="stat-today-active" value={stats.today.active_customers} label="Aktive Kunden heute" />
              <StatCell testid="stat-today-rewards" value={stats.today.rewards_redeemed} label="Belohnungen eingelöst" />
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Gesamt</p>
            <div className="grid grid-cols-2 gap-2">
              <StatCell testid="stat-total-stamps" value={isPoints ? stats.total.points : stats.total.stamps} label={isPoints ? "Punkte gesamt" : "Stempel gesamt"} />
              <StatCell testid="stat-total-customers" value={stats.total.customers} label="Registrierte Kunden" />
              <StatCell testid="stat-total-open" value={stats.total.rewards_open} label="Offene Belohnungen" />
              <StatCell testid="stat-total-redeemed" value={stats.total.rewards_redeemed} label="Eingelöst gesamt" />
            </div>
          </div>
        </div>
      )}

      <div data-testid="staff-audit-section" className="space-y-3">
        <h2 className="font-display font-extrabold text-[17px] text-slate-900 flex items-center gap-2">
          <History className="h-[18px] w-[18px]" style={{ color: "var(--brand-primary)" }} /> Aktivitätsprotokoll
        </h2>
        {canReset && (
          <div data-testid="audit-filter-bar" className="flex flex-wrap items-center gap-2 rounded-2xl bg-white ring-1 ring-slate-100 shadow-soft p-3">
            <input data-testid="audit-filter-date" type="date" value={auditDate}
              onChange={(e) => setAuditDate(e.target.value)}
              className="h-10 rounded-xl ring-1 ring-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-700 outline-none flex-1 min-w-[140px]" />
            <select data-testid="audit-filter-type" value={auditType}
              onChange={(e) => setAuditType(e.target.value)}
              className="h-10 rounded-xl ring-1 ring-slate-200 bg-slate-50 px-3 text-sm font-semibold text-slate-700 outline-none flex-1 min-w-[140px]">
              <option value="">Alle Ereignisse</option>
              <option value="stamp_added">{isPoints ? "Punkte hinzugefügt" : "Stempel hinzugefügt"}</option>
              <option value="reward_created">Belohnung freigeschaltet</option>
              <option value="reward_redeemed">Belohnung eingelöst</option>
            </select>
            {(auditDate || auditType) && (
              <button data-testid="audit-filter-reset" onClick={() => { setAuditDate(""); setAuditType(""); }}
                className="h-10 rounded-xl px-3 text-sm font-bold text-slate-500 hover:bg-slate-100 ring-1 ring-slate-200 press flex items-center gap-1.5">
                <X className="h-4 w-4" /> Filter zurücksetzen
              </button>
            )}
          </div>
        )}
        {auditLog.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-5 text-center text-slate-400 text-sm">
            {(canReset && (auditDate || auditType)) ? "Keine Aktivitäten für diesen Filter." : "Noch keine Aktivitäten."}
          </div>
        ) : (
          <div className="rounded-2xl bg-white ring-1 ring-slate-100 shadow-soft divide-y divide-slate-100 overflow-hidden">
            {auditLog.map((a) => (
              <div key={a.id} data-testid="audit-log-item" className="flex items-start gap-3 px-4 py-3">
                <div className="h-8 w-8 rounded-lg bg-slate-50 ring-1 ring-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                  {a.event_type === "reward_redeemed" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    : a.event_type === "reward_created" ? <Gift className="h-4 w-4" style={{ color: "var(--brand-secondary)" }} />
                    : <Plus className="h-4 w-4" style={{ color: "var(--brand-primary)" }} />}
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-slate-700 leading-tight">
                    <span className="font-display font-bold text-slate-900">{a.detail}</span>{" – "}<span className="text-slate-500">{a.customer_name}</span>
                  </p>
                  <p className="text-[11px] text-slate-400 font-medium mt-0.5">{a.display_time} · {a.staff_name}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div data-testid="staff-maintenance-section" className={`pt-2 ${canReset ? "" : "hidden"}`}>
        <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold px-1 mb-1.5">Administration (nur Inhaber)</p>
        <div className="rounded-2xl bg-white ring-1 ring-rose-100 shadow-soft p-4">
          <div className="flex items-start gap-2.5 mb-3">
            <ShieldAlert className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
            <p className="text-[12px] text-slate-500 leading-relaxed font-medium">Testdaten dieses Betriebs vor dem echten Start entfernen.</p>
          </div>
          <Button data-testid="open-pilot-reset-button" onClick={() => { setResetPhrase(""); setResetOpen(true); }} variant="outline"
            className="w-full h-11 rounded-2xl border-rose-200 text-rose-600 hover:bg-rose-50 font-display font-bold press">
            <Trash2 className="mr-2 h-4 w-4" /> Testdaten zurücksetzen
          </Button>
        </div>
      </div>
        </div>
        )}{/* end secondary column */}
      </div>{/* end cockpit grid */}
      </>)}

      {/* Stamp / purchase dialog */}
      <AlertDialog open={stampConfirm} onOpenChange={setStampConfirm}>
        <AlertDialogContent className={`rounded-3xl ${counterView ? "max-w-md sm:max-w-lg" : "max-w-sm"}`}>
          <AlertDialogHeader>
            <AlertDialogTitle className={`font-display ${counterView ? "text-2xl" : "text-xl"}`}>{isPoints ? "Punkte gutschreiben" : "Stempel gutschreiben"}</AlertDialogTitle>
            <AlertDialogDescription className={`leading-relaxed ${counterView ? "text-base" : ""}`}>
              Für <span className="font-bold text-slate-700">{state?.customer?.name}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {needsAmount && (
            <div className="space-y-1.5 py-1">
              <label className={`font-bold text-slate-500 ${counterView ? "text-base" : "text-xs"}`}>Einkaufsbetrag ({cur})</label>
              <Input data-testid="stamp-amount-input" type="number" min="0" step="0.01" value={amount}
                onChange={(e) => setAmount(e.target.value)} placeholder="z. B. 12.50" autoFocus
                className={`rounded-xl text-center font-display font-bold ${counterView ? "h-16 text-2xl md:text-2xl" : "h-12 text-lg md:text-lg"} text-slate-900`} />
            </div>
          )}
          {!needsAmount && (
            <div className="space-y-1.5 py-1">
              <label className={`font-bold text-slate-500 ${counterView ? "text-base" : "text-xs"}`}>Anzahl Stempel</label>
              <Input data-testid="stamp-quantity-input" type="number" min="1" max="50" value={quantity}
                onChange={(e) => setQuantity(e.target.value)} className={`rounded-xl text-center font-display font-bold ${counterView ? "h-16 text-2xl md:text-2xl" : "h-12 text-lg md:text-lg"} text-slate-900`} />
            </div>
          )}
          <div data-testid="stamp-preview" className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 px-4 py-4 text-center">
            {needsAmount && (
              <div className="mb-3">
                <p className="text-xs sm:text-sm uppercase tracking-wider font-bold text-slate-500">Einkaufsbetrag</p>
                <p data-testid="stamp-preview-amount" className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900 tabular-nums break-words">{amountText || "—"}</p>
              </div>
            )}
            <p className="text-xs sm:text-sm uppercase tracking-wider font-bold text-slate-500">Kunde erhält</p>
            <p data-testid="stamp-preview-result" className="font-display font-extrabold text-4xl sm:text-5xl leading-tight tabular-nums break-words" style={{ color: "var(--brand-primary)" }}>{creditText || "—"}</p>
          </div>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className={`rounded-xl font-semibold ${counterView ? "h-14 text-lg" : "h-11"}`} disabled={submitting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="staff-confirm-stamp-button" onClick={(e) => { e.preventDefault(); doAddStamp(); }}
              disabled={submitting} className={`rounded-xl disabled:opacity-60 press text-white font-bold whitespace-normal h-auto ${counterView ? "min-h-14 text-lg" : "min-h-11"}`} style={{ background: "var(--brand-primary)" }}>
              {submitting ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Wird gebucht…</> : (creditText ? `${creditText} gutschreiben` : `${isPoints ? "Punkte" : "Stempel"} gutschreiben`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!redeemTarget} onOpenChange={(o) => !o && setRedeemTarget(null)}>
        <AlertDialogContent className={`rounded-3xl ${counterView ? "max-w-md sm:max-w-lg" : "max-w-sm"}`}>
          <AlertDialogHeader>
            <div className="mx-auto h-14 w-14 rounded-2xl brand-wave flex items-center justify-center shadow-brand mb-1"><Gift className="h-7 w-7 text-white" /></div>
            <AlertDialogTitle className="font-display text-center text-xl">Belohnung einlösen?</AlertDialogTitle>
            <AlertDialogDescription className="text-center leading-relaxed">
              <span className="font-bold" style={{ color: "var(--brand-primary)" }}>{redeemTarget?.title}</span> jetzt einlösen? Dies kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse gap-2 mt-1">
            <AlertDialogCancel className="rounded-xl h-11 mt-0 font-semibold w-full">Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="staff-confirm-redemption-button" onClick={doRedeem} className="rounded-xl h-11 bg-emerald-600 hover:bg-emerald-700 font-bold w-full press">
              <CheckCircle2 className="mr-1 h-4 w-4" /> Einlösen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resetOpen} onOpenChange={(o) => { if (!resetBusy) { setResetOpen(o); if (!o) setResetPhrase(""); } }}>
        <AlertDialogContent className="rounded-3xl max-w-sm">
          <AlertDialogHeader>
            <div className="mx-auto h-14 w-14 rounded-2xl bg-rose-50 ring-1 ring-rose-100 flex items-center justify-center mb-1"><ShieldAlert className="h-7 w-7 text-rose-500" /></div>
            <AlertDialogTitle className="font-display text-center text-xl">Testdaten zurücksetzen</AlertDialogTitle>
            <AlertDialogDescription className="text-center leading-relaxed">
              Alle Kunden-, Buchungs- und Belohnungsdaten dieses Betriebs werden unwiderruflich gelöscht – einschließlich aller physischen Karten und Kartensätze. Bereits gedruckte Karten werden dadurch ungültig. Marke, Regeln und Team bleiben erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-1">
            <label className="text-xs font-bold text-slate-500">Tippe zur Bestätigung: <span className="font-display font-extrabold text-rose-600">{RESET_PHRASE}</span></label>
            <Input data-testid="pilot-reset-phrase-input" value={resetPhrase} onChange={(e) => setResetPhrase(e.target.value)} placeholder={RESET_PHRASE} disabled={resetBusy} className="h-12 rounded-xl text-center font-display font-bold tracking-wide" />
          </div>
          <AlertDialogFooter className="flex-col-reverse gap-2 mt-1">
            <AlertDialogCancel className="rounded-xl h-11 mt-0 font-semibold w-full" disabled={resetBusy}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction data-testid="pilot-reset-confirm-button" onClick={(e) => { e.preventDefault(); doReset(); }}
              disabled={resetBusy || resetPhrase.trim() !== RESET_PHRASE} className="rounded-xl h-11 bg-rose-600 hover:bg-rose-700 font-bold w-full press disabled:opacity-50">
              {resetBusy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Wird gelöscht…</> : <><Trash2 className="mr-1 h-4 w-4" /> Endgültig löschen</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QrScanner open={scannerOpen} onClose={() => setScannerOpen(false)} onResolved={onQrResolved} business={business} />
    </div>
  );
};

const StatCell = ({ testid, value, label }) => (
  <div data-testid={testid} className="rounded-xl bg-slate-50 ring-1 ring-slate-100 p-3">
    <p className="font-display font-extrabold text-2xl text-slate-900 leading-none">{value}</p>
    <p className="text-[11px] font-semibold text-slate-400 mt-1 leading-tight">{label}</p>
  </div>
);

// Staff-role home: fetches own business context and wraps the cockpit.
export const StaffHome = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [counter, setCounter] = useState(false);
  useEffect(() => { getMyContext().then(setCtx).catch(() => {}); }, []);
  const handleLogout = async () => { await logout(); navigate("/login"); };
  if (!ctx) return <Loading />;
  if (!ctx.business.active) return (
    <BusinessLayout business={ctx.business} right={
      <button data-testid="staff-logout-button" onClick={handleLogout} className="flex items-center gap-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 px-3 py-2 text-xs font-bold press">
        <LogOut className="h-4 w-4" /> Abmelden
      </button>
    }>
      <div data-testid="staff-inactive-notice" className="p-6 pt-10 text-center">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-3"><History className="h-7 w-7 text-amber-600" /></div>
        <p className="font-display font-extrabold text-xl text-slate-900">Betrieb derzeit deaktiviert</p>
        <p className="text-slate-500 text-sm font-medium mt-2 leading-relaxed">Die Kasse ist vorübergehend nicht verfügbar. Bitte wende dich an den Inhaber.</p>
      </div>
    </BusinessLayout>
  );
  return (
    <BusinessLayout business={ctx.business} wide shell={counter} right={
      <div className="flex items-center gap-2">
        <EnvBadge />
        <ChangePasswordButton variant="dark" />
        <button data-testid="staff-logout-button" onClick={handleLogout} className="flex items-center gap-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 px-3 py-2 text-xs font-bold press">
          <LogOut className="h-4 w-4" /> Abmelden
        </button>
      </div>
    }>
      <ShopCockpit business={ctx.business} canReset={ctx.role === "owner"} onCounterViewChange={setCounter} />
    </BusinessLayout>
  );
};
